'use client';

import { ChevronRight, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { SIDEBAR_SECTION_HEADER_ROW_CLASS } from './sidebar-row-metrics';
import { SIDEBAR_SECTION_LABEL_CLASS } from './sidebar-section-label';

/** How many items a collapsed section hides, beside its heading. */
export function CollapsedSectionCount({
  count,
  partial = false,
}: {
  count?: number;
  partial?: boolean;
}) {
  if (!count) return null;
  return (
    <span
      data-sidebar-section-count
      className="text-muted-foreground/60 ms-0.5 text-xs font-medium normal-case tabular-nums"
    >
      {count}
      {partial ? '+' : null}
    </span>
  );
}

/**
 * A sidebar section heading (Pinned, Recent, Direct messages, a channel
 * section): the label toggles the section, a chevron after it shows on hover,
 * a collapsed section shows how many rows it hides, and the trailing actions
 * appear on hover or focus (always on touch screens).
 */
export function SidebarSectionHeader({
  sectionId,
  label,
  expanded,
  onToggle,
  count,
  countPartial = false,
  actions,
  actionsAlwaysVisible = false,
  pending = false,
  ariaLabel,
  className,
}: {
  sectionId: string;
  label: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** Rows the section hides, shown while collapsed ("+" when partial). */
  count?: number;
  countPartial?: boolean;
  actions?: ReactNode;
  /** Keep the actions visible at rest (the Chats heading's tools). */
  actionsAlwaysVisible?: boolean;
  pending?: boolean;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div
      data-sidebar-section={sectionId}
      className={cn(
        'group/section-header justify-between gap-1.5',
        SIDEBAR_SECTION_HEADER_ROW_CLASS,
        className
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={ariaLabel}
        className={cn(
          'text-sidebar-section-foreground hover:text-foreground group/section-heading flex min-w-0 flex-1 items-center gap-1 text-left',
          SIDEBAR_SECTION_LABEL_CLASS
        )}
      >
        <span className="min-w-0 truncate">{label}</span>
        {expanded ? null : (
          <CollapsedSectionCount count={count} partial={countPartial} />
        )}
        <ChevronRight
          aria-hidden="true"
          className={cn(
            'size-3 shrink-0 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/section-heading:opacity-100 group-hover/section-heading:opacity-100',
            expanded && 'rotate-90'
          )}
        />
      </button>
      {pending ? (
        <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
      ) : null}
      {actions ? (
        <div
          data-sidebar-section-actions
          className={cn(
            'flex shrink-0 items-center gap-0.5',
            !actionsAlwaysVisible &&
              'opacity-0 transition-opacity duration-150 group-focus-within/section-header:opacity-100 group-hover/section-header:opacity-100 [@media(hover:none)]:opacity-100'
          )}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}
